/**
 * Content Security Policy configuration for enhanced security
 */

export const CSP_DIRECTIVES = {
  'default-src': ["'self'"],
  'script-src': [
    "'self'",
    "'unsafe-inline'", // Required for Vite in development and React
    "'unsafe-eval'", // Required for development builds
    'https://maps.googleapis.com',
    'https://www.gstatic.com',
    'blob:' // Required for service workers
  ],
  'style-src': [
    "'self'",
    "'unsafe-inline'", // Required for CSS-in-JS and dynamic styles
    'https://fonts.googleapis.com'
  ],
  'img-src': [
    "'self'",
    'data:',
    'blob:',
    'https:',
    'https://*.supabase.co',
    'https://*.googleapis.com',
    'https://*.gstatic.com',
    'https://res.cloudinary.com' // Cloudinary images
  ],
  'font-src': [
    "'self'",
    'data:',
    'https://fonts.gstatic.com'
  ],
  'connect-src': [
    "'self'",
    'https://*.supabase.co',
    'https://api.openai.com',
    'wss://*.supabase.co',
    'https://maps.googleapis.com',
    'https://*.googleapis.com',
    'https://res.cloudinary.com',
    'data:',
    'blob:'
  ],
  'worker-src': [
    "'self'",
    'blob:'
  ],
  'media-src': [
    "'self'",
    'blob:',
    'data:',
    'https://*.supabase.co'
  ],
  'manifest-src': ["'self'"],
  'frame-src': ["'none'"],
  'object-src': ["'none'"],
  'base-uri': ["'self'"],
  'form-action': ["'self'"],
  'frame-ancestors': ["'none'"],
  'upgrade-insecure-requests': [],
  'block-all-mixed-content': []
};

/**
 * Additional security headers for enhanced protection
 */
export const SECURITY_HEADERS = {
  'X-Frame-Options': 'DENY',
  'X-Content-Type-Options': 'nosniff',
  'X-XSS-Protection': '1; mode=block',
  'Referrer-Policy': 'strict-origin-when-cross-origin',
  'Permissions-Policy': 'camera=(self), microphone=(), geolocation=(self), payment=()',
  'Strict-Transport-Security': 'max-age=31536000; includeSubDomains; preload'
};

/**
 * Generate CSP header value
 */
export function generateCSPHeader(): string {
  return Object.entries(CSP_DIRECTIVES)
    .map(([directive, sources]) => {
      if (sources.length === 0) {
        return directive;
      }
      return `${directive} ${sources.join(' ')}`;
    })
    .join('; ');
}

/**
 * Apply CSP and security headers to document head
 */
export function applyCSP() {
  if (typeof document !== 'undefined') {
    // Apply CSP
    const cspMeta = document.createElement('meta');
    cspMeta.httpEquiv = 'Content-Security-Policy';
    cspMeta.content = generateCSPHeader();
    document.head.appendChild(cspMeta);

    // Apply additional security headers via meta tags where possible
    const referrerMeta = document.createElement('meta');
    referrerMeta.name = 'referrer';
    referrerMeta.content = 'strict-origin-when-cross-origin';
    document.head.appendChild(referrerMeta);
  }
}

/**
 * Log CSP violations for monitoring
 */
export function setupCSPReporting() {
  if (typeof document !== 'undefined') {
    document.addEventListener('securitypolicyviolation', async (e) => {
      console.warn('CSP Violation:', {
        blockedURI: e.blockedURI,
        violatedDirective: e.violatedDirective,
        originalPolicy: e.originalPolicy,
        disposition: e.disposition
      });
      
      // Send violation to edge function for logging
      try {
        await fetch('https://dkhqvxbmigvhtzbjhbrf.supabase.co/functions/v1/log-csp-violation', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            blockedURI: e.blockedURI,
            violatedDirective: e.violatedDirective,
            originalPolicy: e.originalPolicy,
            disposition: e.disposition,
          }),
        });
      } catch (error) {
        console.error('Failed to log CSP violation:', error);
      }
    });
  }
}